import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, MoreHorizontal, Plus, Tag } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ApprovalStatusBadge } from '@/components/efs/ApprovalStatusBadge';
import { PurgeMasterDialog } from '@/components/efs/PurgeMasterDialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useCurrentUser } from '@/lib/auth-context';
import { hasPermission } from '@/lib/permissions';
import {
  useActivateNormalizationRule,
  useCreateNormalizationRule,
  usePurgeNormalizationRule,
  useRetireNormalizationRule,
  useNormalizationRulesList,
} from '@/features/masters/normalization/useNormalizationRules';
import type { NormalizationRule } from '@/features/masters/normalization/types';

/**
 * Alias management embedded in the incoming-item editor.
 *
 * A `name_normalization_rules` row IS an alias: `raw_name` (the alias seen in
 * source data) → `target_master_id` (this incoming item). This panel surfaces
 * the rules that target the current item and lets the operator add new aliases
 * pre-pointed at it — so alias management lives where the canonical item is
 * edited, instead of in a disconnected queue.
 */
interface Props {
  incomingItemId: string;
  /** Canonical name used as the alias rule's `normalized_name`. */
  normalizedName: string;
}

const AliasLifecycleActions = ({ alias }: { alias: NormalizationRule }) => {
  const { t } = useTranslation('masters');
  const user = useCurrentUser();
  const retire = useRetireNormalizationRule();
  const activate = useActivateNormalizationRule();
  const purge = usePurgeNormalizationRule();
  const [purgeOpen, setPurgeOpen] = useState(false);

  const canManage = hasPermission(user, 'master.approve');
  const isApproved = alias.human_approval_status === 'approved';
  const isRetired = alias.human_approval_status === 'retired';
  if (!canManage || (!isApproved && !isRetired)) return null;

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7 shrink-0"
            aria-label={t('efs.masters.actions.row_menu')}
          >
            <MoreHorizontal className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {isApproved ? (
            <DropdownMenuItem
              onSelect={() => retire.mutate(alias.normalization_rule_id)}
              disabled={retire.isPending}
            >
              {t('efs.masters.actions.deactivate')}
            </DropdownMenuItem>
          ) : null}
          {isRetired ? (
            <>
              <DropdownMenuItem
                onSelect={() => activate.mutate(alias.normalization_rule_id)}
                disabled={activate.isPending}
              >
                {t('efs.masters.actions.activate')}
              </DropdownMenuItem>
              <DropdownMenuItem
                onSelect={() => {
                  purge.reset();
                  setPurgeOpen(true);
                }}
                className="text-red-900 focus:text-red-900"
              >
                {t('efs.masters.actions.purge')}
              </DropdownMenuItem>
            </>
          ) : null}
        </DropdownMenuContent>
      </DropdownMenu>

      <PurgeMasterDialog
        open={purgeOpen}
        onOpenChange={setPurgeOpen}
        name={alias.raw_name}
        isPending={purge.isPending}
        error={purge.error}
        onConfirm={() =>
          purge.mutate(alias.normalization_rule_id, { onSuccess: () => setPurgeOpen(false) })
        }
      />
    </>
  );
};

export const IncomingItemAliasesPanel = ({ incomingItemId, normalizedName }: Props) => {
  const { t } = useTranslation('masters');
  const [raw, setRaw] = useState('');

  // Backend list filters by master_type only; we narrow to this item's aliases
  // client-side via target_master_id.
  const query = useNormalizationRulesList({ master_type: 'incoming_item', per_page: 200 });
  const create = useCreateNormalizationRule();

  const aliases = useMemo(
    () => (query.data?.rows ?? []).filter((r) => r.target_master_id === incomingItemId),
    [query.data, incomingItemId],
  );

  const addAlias = async () => {
    const rawName = raw.trim();
    if (!rawName) return;
    await create.mutateAsync({
      master_type: 'incoming_item',
      raw_name: rawName,
      normalized_name: normalizedName,
      target_master_id: incomingItemId,
      confidence: 1,
      effective_date: new Date().toISOString().slice(0, 10),
    });
    setRaw('');
  };

  return (
    <div className="space-y-3 rounded-md border border-efs-border p-3">
      <div className="flex items-center gap-2 text-sm font-semibold text-efs-text-primary">
        <Tag className="h-4 w-4" />
        {t('efs.masters.incoming.aliases.title', { defaultValue: '別名' })}
      </div>
      <p className="text-xs text-efs-text-muted">
        {t('efs.masters.incoming.aliases.hint', {
          defaultValue: 'この入荷品名に対応付ける別名（元データに現れる表記ゆれ）を管理します。',
        })}
      </p>

      {query.isLoading ? (
        <Loader2 className="h-4 w-4 animate-spin" />
      ) : aliases.length === 0 ? (
        <p className="text-xs text-efs-text-muted">
          {t('efs.masters.incoming.aliases.empty', { defaultValue: '別名はまだありません' })}
        </p>
      ) : (
        <ul className="space-y-1">
          {aliases.map((a: NormalizationRule) => (
            <li
              key={a.normalization_rule_id}
              className="flex items-center justify-between gap-2 rounded-md border border-efs-border bg-efs-bg-subtle px-2 py-1.5 text-sm"
            >
              <span className="truncate">{a.raw_name}</span>
              <span className="flex shrink-0 items-center gap-1">
                <ApprovalStatusBadge status={a.human_approval_status} />
                <AliasLifecycleActions alias={a} />
              </span>
            </li>
          ))}
        </ul>
      )}

      <div className="flex items-center gap-2">
        <Input
          value={raw}
          onChange={(e) => setRaw(e.target.value)}
          placeholder={t('efs.masters.incoming.aliases.add_placeholder', {
            defaultValue: '別名（元の表記）を入力',
          })}
          className="h-8"
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              void addAlias();
            }
          }}
        />
        <Button
          type="button"
          size="sm"
          variant="outline"
          disabled={!raw.trim() || create.isPending}
          onClick={() => void addAlias()}
        >
          {create.isPending ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Plus className="h-4 w-4" />
          )}
          {t('efs.masters.incoming.aliases.add', { defaultValue: '別名を追加' })}
        </Button>
      </div>
    </div>
  );
};
